import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { AgnosticRouteMatch } from '@remix-run/router';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import type { DeviceType } from '@talos/kyoko';
import {
  Box,
  Divider,
  DropdownMenu,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  Tooltip,
  Tour,
  notEmpty,
  useMixpanel,
} from '@talos/kyoko';
import type { CSSProperties } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toNavGroupName } from '../../hooks/useSpacesNavItems';
import { useAppLayoutConfig } from '../../providers/AppLayoutConfig/AppLayoutConfigContext';
import { useAppStateDispatch, useAppStateStore } from '../../providers/AppStateProvider';
import { groupAcceptsSpaceDrops, type NavigationItem } from '../../tokens/navigationPaths';
import { ShowcaseName } from '../../tokens/showcase';
import { TourName } from '../../tokens/tours';
import type { LayoutSpec } from '../../types/LayoutConfig';
import { getPinSection } from '../../utils/getPinSection';
import { addNewLayoutToSection, selectCurrentLayout } from '../AppLayout/AppLayoutSlice';
import { ShowcaseTrigger } from '../Showcase';
import {
  applyReorder,
  emptyGroupDropId,
  findGroupKeyForId,
  groupLabelToKey,
  parseEmptyGroupDropId,
} from './navigationSidebarReorder';
import { parseSection } from './parseSection';
import { PinMenu } from './PinMenu';
import { SidebarSubMenuNavItem } from './SidebarSubMenuNavItem';
import { ManageSpaceButtons } from './Spaces/ManageSpaceButtons';
import { getLocationAfterSpaceGroupChange } from './spacesNavPath';
import {
  SidebarSubMenu,
  SidebarSubMenuEmptyGroupDrop,
  SidebarSubMenuGroupHeader,
  SidebarSubMenuHeader,
  SidebarSubMenuItems,
  SidebarSubMenuNavItemRowStatic,
  SidebarSubMenuTitle,
  SidebarSubMenuWrapper,
} from './styles';

const PIN_MENU_TARGET_STYLE: CSSProperties = {
  marginRight: '-8px',
  marginLeft: '-8px',
  zIndex: 0,
};

const PIN_TARGET_STYLE: CSSProperties = {
  zIndex: 1,
};

function EmptyGroupDropZone({ groupKey }: { groupKey: string }) {
  const { setNodeRef, isOver } = useDroppable({
    id: emptyGroupDropId(groupKey),
  });
  return (
    <SidebarSubMenuEmptyGroupDrop
      ref={setNodeRef}
      data-testid="sidebar-submenu-empty-group-drop"
      $isOver={isOver}
      aria-hidden
    />
  );
}

export interface NavigationSidebarSubMenuProps {
  /** Mount submenu content (equivalent to `showSubMenu || isPinned` on shell). */
  visible: boolean;
  /** Passed to `SidebarSubMenuWrapper` `expanded` (e.g. `hasSubNavItems && isPinned`). */
  wrapperExpanded: boolean;
  /** Sidebar hover expanded — for tour delay / pin row tour visibility. */
  isExpanded: boolean;
  /** Whether the active top-level route has child nav items. */
  hasSubNavItems: boolean;
  /**
   * Route matches from the parent — do not call `useNavigationItems` here: it runs `useSpacesNavItems`
   * (which updates app config from layout defaults) and must run only from the shell that mounts once.
   */
  topLevelMatch: AgnosticRouteMatch<string, NavigationItem> | undefined;
  groupMatch: AgnosticRouteMatch<string, NavigationItem> | undefined;
}

export const NavigationSidebarSubMenu = ({
  visible,
  wrapperExpanded,
  isExpanded,
  hasSubNavItems,
  topLevelMatch,
  groupMatch,
}: NavigationSidebarSubMenuProps) => {
  const { pinNavigationSidebar, setPinNavigationSidebar } = useDisplaySettings();
  const location = useLocation();
  const layoutSection = parseSection(topLevelMatch?.route?.path);
  const pinSection = getPinSection(location.pathname);

  const [isRearranging, setIsRearranging] = useState(false);
  const [pendingOrderByGroup, setPendingOrderByGroup] = useState<Map<string, string[]>>(() => new Map());
  const [pendingGroups, setPendingGroups] = useState<Map<string, string | undefined>>(new Map());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const pendingOrderByGroupRef = useRef(pendingOrderByGroup);
  const pendingGroupsRef = useRef(pendingGroups);

  useEffect(() => {
    pendingOrderByGroupRef.current = pendingOrderByGroup;
  }, [pendingOrderByGroup]);

  useEffect(() => {
    pendingGroupsRef.current = pendingGroups;
  }, [pendingGroups]);

  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const navigate = useNavigate();
  const store = useAppStateStore();

  const { layoutConfigs, updateSpacesConfigs } = useAppLayoutConfig(layoutSection ?? 'trading');

  const isPinned = pinNavigationSidebar;

  const handlePinChange = useCallback(() => {
    mixpanel.track(MixpanelEvent.ChangeSidebarPin, { pinned: !pinNavigationSidebar, section: pinSection });
    if (pinSection !== '') {
      setPinNavigationSidebar(!isPinned);
    }
  }, [pinNavigationSidebar, mixpanel, pinSection, setPinNavigationSidebar, isPinned]);

  const handleAddNewSpace = useCallback(
    (newSpaceName: string, selectedTemplate: LayoutSpec, deviceType: DeviceType, group?: string) => {
      if (newSpaceName === '') {
        return;
      }
      dispatch(addNewLayoutToSection({ label: newSpaceName, template: selectedTemplate, deviceType, group }));
      const newLayout = selectCurrentLayout(store.getState());
      if (newLayout) {
        const basePath = topLevelMatch?.route?.path;
        const groupSegment = group ? `/${toNavGroupName(group)}` : '';
        navigate(`${basePath}${groupSegment}/${newLayout.id}`);
      }
      mixpanel.track(MixpanelEvent.AddNewSpace, {
        [MixpanelEventProperty.Group]: group,
        [MixpanelEventProperty.LayoutSection]: layoutSection,
        [MixpanelEventProperty.DeviceType]: deviceType,
      });
    },
    [mixpanel, navigate, topLevelMatch, dispatch, store, layoutSection]
  );

  const editableItems = useMemo(() => {
    const items: NavigationItem[] = [];
    for (const group of topLevelMatch?.route?.children ?? []) {
      for (const item of group.children ?? []) {
        if (item.isEditable) {
          items.push(item);
        }
      }
    }
    return items;
  }, [topLevelMatch?.route?.children]);

  const dragOverlayItem = useMemo(
    () => (activeDragId != null ? editableItems.find(i => (i.id ?? i.path) === activeDragId) : undefined),
    [activeDragId, editableItems]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const handleStartRearranging = useCallback(() => {
    const layoutById = Object.fromEntries((layoutConfigs ?? []).map(l => [l.id, l])) as Record<string, LayoutSpec>;
    const pendingGroupsMap = new Map(
      editableItems.map(item => {
        const id = item.id ?? '';
        return [item.id ?? item.path ?? '', id ? layoutById[id]?.group : undefined];
      })
    );
    const globalOrder = editableItems.map(item => item.id ?? item.path ?? '');
    const orderByGroup = new Map<string, string[]>();
    for (const group of topLevelMatch?.route?.children ?? []) {
      const gl = group.label;
      const key = groupLabelToKey(gl);
      const ids = globalOrder.filter(id => pendingGroupsMap.get(id) === gl);
      orderByGroup.set(key, ids);
    }
    setPendingOrderByGroup(orderByGroup);
    setPendingGroups(pendingGroupsMap);
    setIsRearranging(true);
    mixpanel.track(MixpanelEvent.StartSpacesNavRearrange, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });
  }, [editableItems, layoutConfigs, layoutSection, mixpanel, topLevelMatch?.route?.children]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const groupKeyAcceptsSpaceDrops = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const g of topLevelMatch?.route?.children ?? []) {
      m.set(groupLabelToKey(g.label), groupAcceptsSpaceDrops(g));
    }
    return m;
  }, [topLevelMatch?.route?.children]);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) {
        return;
      }
      const overIdStr = String(over.id);
      const emptyDropKey = parseEmptyGroupDropId(overIdStr);
      if (emptyDropKey != null) {
        if (!groupKeyAcceptsSpaceDrops.get(emptyDropKey)) {
          return;
        }
      } else {
        const overKey = findGroupKeyForId(pendingOrderByGroupRef.current, overIdStr);
        if (overKey != null && !groupKeyAcceptsSpaceDrops.get(overKey)) {
          return;
        }
      }
      const result = applyReorder(
        pendingOrderByGroupRef.current,
        pendingGroupsRef.current,
        String(active.id),
        overIdStr
      );
      if (!result) {
        return;
      }
      pendingOrderByGroupRef.current = result.orderByGroup;
      pendingGroupsRef.current = result.groups;
      setPendingOrderByGroup(result.orderByGroup);
      setPendingGroups(result.groups);
    },
    [groupKeyAcceptsSpaceDrops]
  );

  const handleDragEnd = useCallback(() => {
    mixpanel.track(MixpanelEvent.MovedSpacesNavItem, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });
    setActiveDragId(null);
  }, [layoutSection, mixpanel]);

  const handleSaveRearrange = useCallback(() => {
    if (!layoutConfigs || !layoutSection) {
      setIsRearranging(false);
      return;
    }
    const orderedIds: string[] = [];
    for (const g of topLevelMatch?.route?.children ?? []) {
      orderedIds.push(...(pendingOrderByGroup.get(groupLabelToKey(g.label)) ?? []));
    }
    const pendingSet = new Set(orderedIds);
    for (const l of layoutConfigs) {
      if (!pendingSet.has(l.id)) {
        orderedIds.push(l.id);
      }
    }
    const layoutByIdForSave = Object.fromEntries(layoutConfigs.map(l => [l.id, l])) as Record<string, LayoutSpec>;

    const nextLayouts = orderedIds
      .map(id => {
        const l = layoutByIdForSave[id];
        if (l == null) {
          return null;
        }
        const g = pendingGroups.has(id) ? pendingGroups.get(id) : l.group;
        const { group: _prev, ...rest } = l;
        return g != null && g !== '' ? ({ ...rest, group: g } as LayoutSpec) : (rest as LayoutSpec);
      })
      .filter(notEmpty);

    updateSpacesConfigs(nextLayouts);
    mixpanel.track(MixpanelEvent.SaveSpacesNavRearrange, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });

    if (topLevelMatch?.route?.enableSpaces && layoutSection && layoutConfigs?.length) {
      const nextLocation = getLocationAfterSpaceGroupChange(
        location.pathname,
        location.search,
        location.hash,
        layoutSection,
        nextLayouts
      );
      if (nextLocation) {
        navigate(nextLocation, { replace: true });
      }
    }

    setIsRearranging(false);
  }, [
    layoutConfigs,
    layoutSection,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    pendingOrderByGroup,
    pendingGroups,
    topLevelMatch?.route?.children,
    topLevelMatch?.route?.enableSpaces,
    updateSpacesConfigs,
    mixpanel,
  ]);

  const handleCancelRearrange = useCallback(() => {
    mixpanel.track(MixpanelEvent.CancelSpacesNavRearrange, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });
    setIsRearranging(false);
    setPendingOrderByGroup(new Map());
    setPendingGroups(new Map());
    setActiveDragId(null);
  }, [layoutSection, mixpanel]);

  useEffect(() => {
    setIsRearranging(false);
    setPendingOrderByGroup(new Map());
    setPendingGroups(new Map());
    setActiveDragId(null);
  }, [layoutSection]);

  const getGroupItems = useCallback(
    (group: NavigationItem): NavigationItem[] => {
      const originalItems = group.children ?? [];
      if (!isRearranging || originalItems.every((i: NavigationItem) => !i.isEditable)) {
        return originalItems;
      }

      const groupLabel = group.label;
      const groupKey = groupLabelToKey(groupLabel);
      const idOrder = pendingOrderByGroup.get(groupKey) ?? [];

      const nonEditable = originalItems.filter((i: NavigationItem) => !i.isEditable);

      const byId = new Map(editableItems.map(item => [item.id ?? item.path ?? '', item] as const));
      const editable = idOrder.map(id => byId.get(id)).filter(notEmpty);

      return [...nonEditable, ...editable];
    },
    [isRearranging, editableItems, pendingOrderByGroup]
  );

  const renderSubMenuItems = () => {
    const groups = topLevelMatch?.route?.children ?? [];
    const content = groups
      .map((group: NavigationItem) => {
        const groupItems = getGroupItems(group);

        const navItem = (item: NavigationItem) => (
          <SidebarSubMenuNavItem
            key={item.path}
            item={item}
            layoutSection={layoutSection}
            rearrangeModeActive={isRearranging}
            onStartRearranging={handleStartRearranging}
          />
        );

        const groupKey = groupLabelToKey(group.label);
        const acceptsDrops = groupKeyAcceptsSpaceDrops.get(groupKey) ?? false;
        const nonEditable = groupItems.filter((i: NavigationItem) => !i.isEditable);
        const editable = groupItems.filter((i: NavigationItem) => i.isEditable);
        const sortableIdsForGroup = pendingOrderByGroup.get(groupKey) ?? [];

        return (
          <Fragment key={group.label ?? 'undefined'}>
            {group.label && (
              <SidebarSubMenuGroupHeader
                data-testid="sidebar-submenu-group-header"
                data-active={groupMatch?.route?.path === group.path}
                active={groupMatch?.route?.path === group.path}
                onClick={() => {
                  mixpanel.track(MixpanelEvent.NavigateTo, {
                    path: group.path,
                    source: MixpanelEventSource.NavigationSidebar,
                  });
                  if (group.mixpanelEvent) {
                    mixpanel.track(group.mixpanelEvent);
                  }
                }}
              >
                {group.label}
              </SidebarSubMenuGroupHeader>
            )}
            {nonEditable.map(navItem)}
            {isRearranging ? (
              <SortableContext items={sortableIdsForGroup} strategy={verticalListSortingStrategy}>
                {editable.map(navItem)}
                {sortableIdsForGroup.length === 0 && acceptsDrops && <EmptyGroupDropZone groupKey={groupKey} />}
              </SortableContext>
            ) : (
              editable.map(navItem)
            )}
          </Fragment>
        );
      })
      .filter(notEmpty);

    if (isRearranging) {
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {content}
          <DragOverlay dropAnimation={null}>
            {dragOverlayItem ? (
              <SidebarSubMenuNavItemRowStatic
                style={{
                  cursor: 'grabbing',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
                  opacity: 0.96,
                }}
              >
                <Box flex="1 1 0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  <Text>{dragOverlayItem.label}</Text>
                </Box>
              </SidebarSubMenuNavItemRowStatic>
            ) : null}
          </DragOverlay>
        </DndContext>
      );
    }

    return content;
  };

  return (
    <SidebarSubMenuWrapper data-testid="sidebar-submenu-wrapper" expanded={wrapperExpanded}>
      {visible && (
        <SidebarSubMenu data-testid="sidebar-submenu">
          <SidebarSubMenuHeader>
            <SidebarSubMenuTitle data-testid="sidebar-submenu-title">{topLevelMatch?.route?.label}</SidebarSubMenuTitle>
            {hasSubNavItems && (isExpanded || isPinned) && (
              <Tour tour={TourName.v255UpdatedNavigationSidebarPin} delay={5000} />
            )}
            <HStack>
              <Tooltip
                tooltip={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
                placement="left"
                targetStyle={PIN_TARGET_STYLE}
              >
                <IconButton
                  data-testid="sidebar-pin-button"
                  data-pin-state={isPinned ? 'pinned' : 'unpinned'}
                  size={FormControlSizes.Small}
                  ghost
                  dim
                  icon={isPinned ? IconName.Pin : IconName.PinOff}
                  onClick={handlePinChange}
                />
              </Tooltip>
              <DropdownMenu
                triggerElement={
                  <IconButton
                    data-testid="sidebar-pin-menu-trigger"
                    ghost
                    size={FormControlSizes.Small}
                    dim
                    icon={IconName.TriangleDown}
                  />
                }
                trigger="click"
                placement="bottom-end"
                usePortal
                targetStyle={PIN_MENU_TARGET_STYLE}
                dropdownProps={{ 'data-testid': 'pin-menu' }}
              >
                <PinMenu />
              </DropdownMenu>
            </HStack>
          </SidebarSubMenuHeader>
          <SidebarSubMenuItems data-testid="sidebar-submenu-items">
            {renderSubMenuItems()}
            {topLevelMatch?.route?.enableSpaces && (
              <>
                <ShowcaseTrigger showcase={ShowcaseName.v256TradingSpaces} />
                <Divider my="spacing8" color="backgroundDivider" />
                {layoutSection && (
                  <ManageSpaceButtons
                    onAddNewSpace={handleAddNewSpace}
                    layoutSection={layoutSection}
                    isRearranging={isRearranging}
                    onSaveRearrange={handleSaveRearrange}
                    onCancelRearrange={handleCancelRearrange}
                  />
                )}
              </>
            )}
          </SidebarSubMenuItems>
        </SidebarSubMenu>
      )}
    </SidebarSubMenuWrapper>
  );
};
